import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import PropTypes from 'prop-types';
/**
 * Here we need to use loadable for now because React.lazy is
 * not supporting SSR yet.
 */

import loadable from '@loadable/component';
import { retry } from './utils';
import { getDisplayName } from '../hocUtils';
var validTooltipProps = ['flip', 'fixed', 'placement', 'timeout', 'appendTo', 'maxWidth', 'zIndex', 'hideDelay', 'showDelay', 'showTooltip'];

var omit = function omit(props, remove) {
  return Object.keys(props).filter(function (prop) {
    return !remove.includes(prop);
  }).reduce(function (res, key) {
    return _objectSpread(_objectSpread({}, res), {}, _defineProperty({}, key, props[key]));
  }, {});
};

var fallbackEllipsis = {
  display: 'inline-block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: '100%',
  verticalAlign: 'bottom',
  whiteSpace: 'noWrap'
};
var LazyEllipsisHOC = loadable(function () {
  return retry(function () {
    return import(
    /* webpackChunkName: "wsr-ellipsisHOC" */
    './EllipsisHOC');
  });
});

var Comp
/** @autodocs-component */
= function Comp(Component) {
  var displayName = getDisplayName(Component);
  var Ellipsed = React.memo(React.forwardRef(function (_ref, ref) {
    var ellipsis = _ref.ellipsis,
        props = _objectWithoutProperties(_ref, ["ellipsis"]);

    var rest = omit(props, validTooltipProps);

    if (ellipsis) {
      return /*#__PURE__*/React.createElement(LazyEllipsisHOC, {
        ref: ref,
        fallback: /*#__PURE__*/React.createElement(Component, _extends({
          style: fallbackEllipsis,
          "data-fallback": true,
          ref: ref
        }, rest)),
        Component: Component,
        props: props
      });
    }

    return /*#__PURE__*/React.createElement(Component, _extends({
      ref: ref
    }, rest));
  }));
  Ellipsed.displayName = displayName;
  return Ellipsed;
};

Comp.propTypes = {
  /** should the text get ellipsed with tooltip, or should it get broken into lines when it reaches the end of its container */
  ellipsis: PropTypes.bool,

  /** `ellipsis` prop. Tooltip content calculation relation to a dom element. Can be either:
   *  `'window', 'scrollParent', 'viewport', 'parent'`, `element` or
   * `function` based predicate i.e. (elm) =>
   *  elm.getAttribute('data-hook') === 'value'
   */
  appendTo: PropTypes.oneOfType([PropTypes.oneOf(['window', 'scrollParent', 'viewport', 'parent']), PropTypes.element, PropTypes.func]),

  /** `ellipsis` prop. Whether to enable the flip behaviour. This behaviour is used to flip the Tooltips placement when it starts to overlap the target element. */
  flip: PropTypes.bool,

  /** `ellipsis` prop. Whether to enable the fixed behaviour. This behaviour is used to keep the Tooltip at it's original placement even when it's being positioned outside the boundary. */
  fixed: PropTypes.bool,

  /** `ellipsis` prop. Tooltip content placement in relation to target element */
  placement: PropTypes.string,

  /** `ellipsis` prop. Tooltip timeout value. */
  timeout: PropTypes.number,

  /** `ellipsis` prop. Tooltip content max width value. */
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** `ellipsis` prop. Tooltip content zIndex. */
  zIndex: PropTypes.number,

  /** `ellipsis` prop. Tooltip hide delay. */
  hideDelay: PropTypes.number,

  /** `ellipsis` prop. Tooltip show delay. */
  showDelay: PropTypes.number,

  /** `ellipsis` prop. Whether to enable the tooltip when an ellipsis is necessary */
  showTooltip: PropTypes.bool
};
export default Comp;